import React, { useEffect, useState } from "react";
import { NavLink, useNavigate, Outlet, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Logo } from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuLabel, DropdownMenuSeparator } from "@/components/ui/dropdown-menu";
import { syncQueue, getQueue, isOnline } from "@/lib/offline";
import { toast } from "sonner";
import {
  LayoutDashboard, ShoppingCart, ReceiptText, Undo2, Package, Truck,
  ClipboardList, Undo, Users, Building2, Wallet, BookOpen, BarChart3,
  Settings as SettingsIcon, Menu, LogOut, Wifi, WifiOff, MoreHorizontal, ChevronRight,
  Building2 as Building2Icon, ChevronDown, Plus, FileCheck2, Repeat, Palette, UserCog, Banknote, ScrollText, FileText, ChevronLeft
} from "lucide-react";

const NAV = [
  { label: "Dashboard", path: "/dashboard", icon: LayoutDashboard, perm: "dashboard" },
  { label: "Billing / New Sale", path: "/billing", icon: ShoppingCart, perm: "billing" },
  { label: "Bill History", path: "/bills", icon: ReceiptText, perm: "bill_history" },
  { label: "Quotation History", path: "/quotations", icon: FileText, perm: "quotation" },
  { label: "Sales Return", path: "/sales-return", icon: Undo2, perm: "sales_return" },
  { label: "Products / Inventory", path: "/products", icon: Package, perm: "products_view" },
  { label: "Purchase", path: "/purchase", icon: Truck, perm: "purchase" },
  { label: "Purchase History", path: "/purchase-history", icon: ClipboardList, perm: "purchase_history" },
  { label: "Purchase Return", path: "/purchase-return", icon: Undo, perm: "purchase_return" },
  { label: "Customers", path: "/customers", icon: Users, perm: "customers" },
  { label: "Vendors", path: "/vendors", icon: Building2, perm: "vendors" },
  { label: "Expenses", path: "/expenses", icon: Wallet, perm: "expenses" },
  { label: "DayBook", path: "/daybook", icon: BookOpen, perm: "daybook" },
  { label: "Reports", path: "/reports", icon: BarChart3, perm: "reports" },
  { label: "GST e-Invoice", path: "/gst", icon: FileCheck2, perm: "gst_compliance" },
  { label: "Automation", path: "/automation", icon: Repeat, perm: "reminders" },
  { label: "Invoice Theme", path: "/invoice-theme", icon: Palette, perm: "settings" },
  { label: "Staff & Payroll", path: "/staff", icon: UserCog, perm: "staff" },
  { label: "Payout Settings", path: "/payout-settings", icon: Banknote, perm: "staff_payment" },
  { label: "My Payroll", path: "/my-payroll", icon: ScrollText, perm: "staff_self" },
  { label: "Settings", path: "/settings", icon: SettingsIcon, perm: "settings" },
];

const MOBILE_PRIMARY = ["/dashboard", "/billing", "/bills", "/products"];

function NavItems({ items, onNavigate }) {
  return (
    <nav className="space-y-1">
      {items.map((it) => {
        const Icon = it.icon;
        return (
          <NavLink
            key={it.path}
            to={it.path}
            onClick={onNavigate}
            data-testid={`nav-${it.path.replace("/", "")}`}
            className={({ isActive }) =>
              `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all ${
                isActive
                  ? "bg-[#F4571E] text-white shadow-sm"
                  : "text-slate-300 hover:bg-white/10 hover:text-white"
              }`
            }
          >
            <Icon className="h-[18px] w-[18px] shrink-0" />
            <span className="truncate">{it.label}</span>
          </NavLink>
        );
      })}
    </nav>
  );
}

export function Layout() {
  const { user, logout, hasPerm, businesses, activeBusiness, switchBusiness } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [online, setOnline] = useState(isOnline());
  const [pending, setPending] = useState(getQueue().length);
  const [moreOpen, setMoreOpen] = useState(false);
  const collapsible = location.pathname === "/detailed-invoice";
  const [collapsedPref, setCollapsedPref] = useState(() => localStorage.getItem("billone_sidebar_collapsed") === "1");
  const collapsed = collapsible && collapsedPref;
  const toggleSidebar = () => { const v = !collapsedPref; setCollapsedPref(v); localStorage.setItem("billone_sidebar_collapsed", v ? "1" : "0"); };

  const items = NAV.filter((n) => hasPerm(n.perm));

  useEffect(() => {
    async function handleOnline() {
      setOnline(true);
      const res = await syncQueue();
      setPending(res.pending);
      if (res.synced > 0) toast.success(`${res.synced} offline bill(s) synced to cloud`);
    }
    function handleOffline() {
      setOnline(false);
      toast.warning("You are offline. Billing will continue and sync later.");
    }
    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);
    const t = setInterval(() => setPending(getQueue().length), 4000);
    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
      clearInterval(t);
    };
  }, []);

  const initials = (user?.name || "U").split(" ").map((s) => s[0]).slice(0, 2).join("").toUpperCase();
  const mobileItems = items.filter((i) => MOBILE_PRIMARY.includes(i.path));
  const moreItems = items.filter((i) => !MOBILE_PRIMARY.includes(i.path));

  return (
    <div className="min-h-screen flex bg-[#F1F5F9]">
      {/* Desktop sidebar */}
      <aside className={`${collapsed ? "hidden" : "hidden lg:flex"} lg:flex-col w-64 xl:w-72 bg-[#0F1A3D] shrink-0 fixed inset-y-0 z-30`} data-testid="sidebar">
        <div className="h-20 flex items-center px-5 border-b border-white/10 relative">
          <div className="bg-white rounded-lg p-1.5">
            <Logo size={34} />
          </div>
          <div className="ml-3 leading-none">
            <div className="font-display font-extrabold text-white text-lg">Bill<span className="text-[#F4571E]">One</span></div>
            <div className="text-[8px] tracking-[0.22em] text-slate-400 font-semibold mt-1">BILLING MADE SIMPLE</div>
          </div>
          {collapsible && (
            <button onClick={toggleSidebar} title="Collapse sidebar" className="absolute right-3 top-1/2 -translate-y-1/2 h-8 w-8 rounded-lg bg-white/10 hover:bg-white/20 text-white flex items-center justify-center transition-colors" data-testid="sidebar-collapse-btn">
              <ChevronLeft className="h-4 w-4" />
            </button>
          )}
        </div>
        <div className="flex-1 overflow-y-auto px-3 py-4">
          <NavItems items={items} />
        </div>
        <div className="p-3 border-t border-white/10">
          <div className="flex items-center gap-2 px-2 text-xs text-slate-400">
            {online ? <Wifi className="h-3.5 w-3.5 text-emerald-400" /> : <WifiOff className="h-3.5 w-3.5 text-amber-400" />}
            {online ? "Online" : "Offline"}{pending > 0 && ` · ${pending} pending`}
          </div>
        </div>
      </aside>

      {/* Main */}
      <div className={`flex-1 ${collapsed ? "" : "lg:ml-64 xl:ml-72"} flex flex-col min-w-0`}>
        {/* Top bar */}
        <header className="h-16 md:h-20 bg-white border-b border-slate-200 flex items-center justify-between px-4 md:px-6 sticky top-0 z-20 no-print">
          <div className="flex items-center gap-3">
            {collapsed && (
              <button onClick={toggleSidebar} title="Expand sidebar" className="hidden lg:flex h-9 w-9 rounded-lg bg-[#0F1A3D] hover:bg-[#1A2A5E] text-white items-center justify-center transition-colors" data-testid="sidebar-expand-btn">
                <ChevronRight className="h-4 w-4" />
              </button>
            )}
            <Sheet>
              <SheetTrigger asChild>
                <Button variant="ghost" size="icon" className="lg:hidden" data-testid="mobile-menu-trigger">
                  <Menu className="h-5 w-5" />
                </Button>
              </SheetTrigger>
              <SheetContent side="left" className="bg-[#0F1A3D] border-0 p-0 w-72">
                <div className="h-16 flex items-center px-5 border-b border-white/10">
                  <div className="bg-white rounded-lg p-1"><Logo size={30} /></div>
                  <span className="ml-3 font-display font-bold text-white">Bill<span className="text-[#F4571E]">One</span></span>
                </div>
                <div className="p-3 overflow-y-auto h-[calc(100%-4rem)]">
                  <NavItems items={items} onNavigate={() => {}} />
                </div>
              </SheetContent>
            </Sheet>
            <div className="lg:hidden"><Logo size={30} /></div>
            <h1 className="hidden sm:block font-display font-semibold text-[#1A2A5E] text-lg">
              {NAV.find((n) => n.path === location.pathname)?.label || "BillOne"}
            </h1>
          </div>

          <div className="flex items-center gap-3">
            {businesses && businesses.length > 0 && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button className="hidden sm:flex items-center gap-1.5 text-sm font-medium text-[#1A2A5E] bg-slate-50 hover:bg-slate-100 rounded-lg px-3 py-1.5 border border-slate-200" data-testid="business-switcher">
                    <Building2Icon className="h-4 w-4" />
                    <span className="max-w-[140px] truncate">{businesses.find((b) => b.id === activeBusiness)?.name || "Business"}</span>
                    <ChevronDown className="h-3.5 w-3.5" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-60 bg-white/95 backdrop-blur-md">
                  <DropdownMenuLabel>Switch Business</DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  {businesses.map((b) => (
                    <DropdownMenuItem key={b.id} onClick={() => switchBusiness(b.id)} data-testid={`switch-biz-${b.id}`} className={b.id === activeBusiness ? "bg-[#F4571E]/10 text-[#F4571E]" : ""}>
                      <Building2Icon className="h-4 w-4 mr-2" /> {b.name}
                    </DropdownMenuItem>
                  ))}
                  {user?.role === "super_admin" && (
                    <>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem onClick={() => navigate("/settings")} data-testid="create-business-link" className="text-[#1A2A5E] font-medium">
                        <Plus className="h-4 w-4 mr-2" /> Create New Business
                      </DropdownMenuItem>
                    </>
                  )}
                </DropdownMenuContent>
              </DropdownMenu>
            )}
            <div className={`hidden sm:flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full ${online ? "bg-emerald-50 text-emerald-700" : "bg-amber-50 text-amber-700"}`}>
              {online ? <Wifi className="h-3.5 w-3.5" /> : <WifiOff className="h-3.5 w-3.5" />}
              {online ? "Online" : "Offline"}
            </div>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="flex items-center gap-2" data-testid="user-menu-trigger">
                  <Avatar className="h-9 w-9 bg-[#1A2A5E]">
                    <AvatarFallback className="bg-[#1A2A5E] text-white text-xs font-semibold">{initials}</AvatarFallback>
                  </Avatar>
                  <div className="hidden md:block text-left leading-tight">
                    <div className="text-sm font-semibold text-slate-800">{user?.name}</div>
                    <div className="text-[11px] text-slate-400 capitalize">{user?.display_role}</div>
                  </div>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-52 bg-white/95 backdrop-blur-md">
                <DropdownMenuLabel>
                  <div className="text-sm">{user?.name}</div>
                  <div className="text-xs text-slate-400 font-normal">{user?.email}</div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                {hasPerm("settings") && (
                  <DropdownMenuItem onClick={() => navigate("/settings")} data-testid="menu-settings">
                    <SettingsIcon className="h-4 w-4 mr-2" /> Settings
                  </DropdownMenuItem>
                )}
                <DropdownMenuItem onClick={logout} data-testid="logout-button" className="text-red-600">
                  <LogOut className="h-4 w-4 mr-2" /> Logout
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>

        <main className="flex-1 p-4 md:p-6 lg:p-8 pb-24 lg:pb-8 min-w-0">
          <Outlet />
        </main>

        {/* Mobile bottom nav */}
        <nav className="lg:hidden fixed bottom-0 inset-x-0 bg-white border-t border-slate-200 flex items-center justify-around h-16 z-20 no-print">
          {mobileItems.map((it) => {
            const Icon = it.icon;
            const active = location.pathname === it.path;
            return (
              <button key={it.path} onClick={() => navigate(it.path)} data-testid={`bottomnav-${it.path.replace("/", "")}`}
                className={`flex flex-col items-center gap-0.5 text-[10px] font-medium ${active ? "text-[#F4571E]" : "text-slate-500"}`}>
                <Icon className="h-5 w-5" />
                {it.label.split(" ")[0]}
              </button>
            );
          })}
          {moreItems.length > 0 && (
            <Sheet open={moreOpen} onOpenChange={setMoreOpen}>
              <SheetTrigger asChild>
                <button className="flex flex-col items-center gap-0.5 text-[10px] font-medium text-slate-500" data-testid="bottomnav-more">
                  <MoreHorizontal className="h-5 w-5" /> More
                </button>
              </SheetTrigger>
              <SheetContent side="bottom" className="bg-white rounded-t-2xl max-h-[70vh] overflow-y-auto">
                <div className="grid grid-cols-2 gap-2 pt-4">
                  {moreItems.map((it) => {
                    const Icon = it.icon;
                    return (
                      <button key={it.path} onClick={() => { navigate(it.path); setMoreOpen(false); }}
                        className="flex items-center gap-3 p-3 rounded-xl border border-slate-200 text-sm font-medium text-slate-700 active:bg-slate-50">
                        <Icon className="h-5 w-5 text-[#1A2A5E]" /> <span className="text-left flex-1">{it.label}</span>
                        <ChevronRight className="h-4 w-4 text-slate-300" />
                      </button>
                    );
                  })}
                </div>
              </SheetContent>
            </Sheet>
          )}
        </nav>
      </div>
    </div>
  );
}
